import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { NotificationProvider } from './context/NotificationContext';
import './css/SystemTheme.css';
import './css/App.css';
import Home from './components/home/Home'; // eager — merges Home.css into main.css, eliminating the async chunk CSS race
import { hidePageLoader } from './utils/pageLoader';

const AppLoading = () => <div className="app-loading">Loading...</div>;

// Auth Components (lazy)
const Login = lazy(() => import('./components/auth/Login'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const AdminResetPassword = lazy(() => import('./components/auth/adminResetPassword'));

// Page Components (lazy)
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const Connection = lazy(() => import('./components/connection/Connection'));
const CalendarV2 = lazy(() => import('./components/calendar/CalendarV2'));
const Web = lazy(() => import('./components/web/Web'));
const Journal = lazy(() => import('./components/journal/Journal'));
const Subscription = lazy(() => import('./components/subscription/Subscription'));
const Settings = lazy(() => import('./components/settings/Settings'));
const Appearance = lazy(() => import('./components/settings/Appearance'));
const Account = lazy(() => import('./components/settings/Account'));
const LoginSecurity = lazy(() => import('./components/settings/LoginSecurity'));
const Privacy = lazy(() => import('./components/settings/Privacy'));
const Notifications = lazy(() => import('./components/settings/Notifications'));
const Help = lazy(() => import('./components/settings/Help'));
const Birthdays = lazy(() => import('./components/birthdays/Birthdays'));
const About = lazy(() => import('./components/web/About'));
const CookiePolicy = lazy(() => import('./components/web/CookiePolicy'));
const Contact = lazy(() => import('./components/web/Contact'));
const Pricing = lazy(() => import('./components/web/Pricing'));
const Features = lazy(() => import('./components/web/Features'));
const Blog = lazy(() => import('./components/web/Blog'));
const UseCases = lazy(() => import('./components/web/UseCases'));
const PublicLayout = lazy(() => import('./components/web/PublicLayout'));
const NotFound = lazy(() => import('./components/web/NotFound'));
const Changelog = lazy(() => import('./components/web/Changelog'));
const Security = lazy(() => import('./components/web/Security'));
const Terms = lazy(() => import('./components/web/Terms'));
const Demo = lazy(() => import('./components/web/Demo'));
const InOutPage = lazy(() => import('./components/settings/InOutPage'));
const VoiceNotesPage = lazy(() => import('./components/settings/VoiceNotesPage'));

// Admin Components (lazy)
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const AdminUserDetail = lazy(() => import('./components/admin/AdminUserDetail')); // Keep for the detail route
const Layout = lazy(() => import('./components/layout/Layout'));
const CookieBanner = lazy(() => import('./components/common/CookieBanner'));
const ScrollToTop = lazy(() => import('./components/common/ScrollToTop'));

const EdgeAIPage = lazy(() => import('./pages/EdgeAI'));
const EdgeAISettings = lazy(() => import('./components/edgeai/EdgeAISettings'));

// Redirects unauthenticated users to /login, preserving the intended destination
const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <Outlet />;
};

// Admin Route Guard
const AdminRoute = () => {
  const { isAdmin } = useAuth();
  return isAdmin ? <Outlet /> : <Navigate to="/dashboard" />;
};

// Main application routes component
function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading) hidePageLoader();
  }, [isLoading]);

  if (isLoading) {
    return <AppLoading />;
  }

  return (
    <Routes>
      {/* Public routes that don't have the main layout */}
      <Route path="/" element={!isAuthenticated ? <Home /> : <Navigate to="/dashboard" />} />
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
      <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
      {/* Public marketing pages — wrapped in PublicLayout (nav + footer + ambient bg) */}
      <Route element={<PublicLayout><Outlet /></PublicLayout>}>
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/security" element={<Security />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/demo" element={<Demo />} />
      </Route>
      {/* /privacy is a settings component — accessed both as public URL and inside authenticated settings */}
      <Route path="/privacy" element={<Privacy />} />

      {/* Protected routes — ProtectedRoute redirects to /login (with state.from) when not authenticated */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/connection" element={<Connection />} />
          <Route path="/calendar" element={<CalendarV2 />} />
          <Route path="/web" element={<Web />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/birthdays" element={<Birthdays />} />
          <Route path="/edgeai" element={<EdgeAIPage />} />
          <Route path="/settings/edgeai" element={<EdgeAISettings />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/appearance" element={<Appearance />} />
          <Route path="/settings/account" element={<Account />} />
          <Route path="/settings/notifications" element={<Notifications />} />
          <Route path="/settings/login-security" element={<LoginSecurity />} />
          <Route path="/settings/in-out" element={<InOutPage />} />
          <Route path="/settings/voice-notes/:noteId?" element={<VoiceNotesPage />} />
          <Route path="/settings/privacy" element={<Privacy />} />
          <Route path="/settings/help" element={<Help />} />
          <Route path="/admin" element={<AdminRoute />}>
            <Route index element={<AdminPanel />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="reset-password" element={<AdminResetPassword />} />
          </Route>
        </Route>
      </Route>

      {/* 404 catch-all */}
      <Route path="*" element={
        isAuthenticated
          ? <Navigate to="/dashboard" />
          : <PublicLayout><NotFound /></PublicLayout>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider> {/* AuthProvider should wrap the Router */}
        <FeatureFlagProvider>
          <NotificationProvider>
            <Router>
              <Suspense fallback={<AppLoading />}>
                <ScrollToTop />
                <AppRoutes />
                <CookieBanner />
              </Suspense>
            </Router>
          </NotificationProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
